import { useEffect, useState, useMemo } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import {
  Ship, LogOut, Search, Download, Mail, Phone, Globe2, MessageSquare, RefreshCw, Loader2, Package,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { authApi, formatApiError } from "@/lib/auth";

function toCsv(rows, columns) {
  const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const header = columns.map((c) => escape(c.label)).join(",");
  const body = rows.map((r) => columns.map((c) => escape(r[c.key])).join(",")).join("\n");
  return header + "\n" + body;
}

function download(filename, content) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function fmtDate(iso) {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function Admin() {
  const [loading, setLoading] = useState(true);
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [qs, cs] = await Promise.all([authApi.getQuotes(), authApi.getContacts()]);
      setQuotes(qs);
      setContacts(cs);
    } catch (err) {
      if (err?.response?.status === 401) {
        authApi.logout();
        navigate("/login", { replace: true });
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filteredQuotes = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return quotes;
    return quotes.filter((r) =>
      [r.name, r.email, r.phone, r.country, r.product, r.quantity, r.message]
        .join(" ").toLowerCase().includes(term)
    );
  }, [q, quotes]);

  const filteredContacts = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return contacts;
    return contacts.filter((r) =>
      [r.name, r.email, r.subject, r.message]
        .join(" ").toLowerCase().includes(term)
    );
  }, [q, contacts]);

  if (!authApi.isAuthed()) return <Navigate to="/login" replace />;

  const logout = () => {
    authApi.logout();
    toast.success("Signed out");
    navigate("/login", { replace: true });
  };

  const exportQuotes = () => {
    const csv = toCsv(filteredQuotes, [
      { key: "created_at", label: "Date" },
      { key: "name", label: "Name" },
      { key: "email", label: "Email" },
      { key: "phone", label: "Phone" },
      { key: "country", label: "Country" },
      { key: "product", label: "Product" },
      { key: "quantity", label: "Quantity" },
      { key: "message", label: "Message" },
    ]);
    download(`quotes_${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };

  const exportContacts = () => {
    const csv = toCsv(filteredContacts, [
      { key: "created_at", label: "Date" },
      { key: "name", label: "Name" },
      { key: "email", label: "Email" },
      { key: "subject", label: "Subject" },
      { key: "message", label: "Message" },
    ]);
    download(`contacts_${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-[#1e5ba8] flex items-center justify-center">
              <Ship className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-display font-bold text-lg text-[#0f172a]">EXIMXPRESS Admin</div>
              <div className="text-xs text-slate-500">Quotes &amp; Contacts Dashboard</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              disabled={loading}
              data-testid="admin-refresh-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              <span className="ml-2 hidden sm:inline">Refresh</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={logout}
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4" />
              <span className="ml-2 hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stat cards */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="stat-quotes">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Total Quotes</div>
                <div className="text-3xl font-display font-bold text-[#0f172a] mt-1">{quotes.length}</div>
              </div>
              <div className="w-11 h-11 rounded-md bg-sky-50 text-[#1e5ba8] flex items-center justify-center">
                <Package className="w-5 h-5" />
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="stat-contacts">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Total Contacts</div>
                <div className="text-3xl font-display font-bold text-[#0f172a] mt-1">{contacts.length}</div>
              </div>
              <div className="w-11 h-11 rounded-md bg-sky-50 text-[#1e5ba8] flex items-center justify-center">
                <MessageSquare className="w-5 h-5" />
              </div>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search by name, email, product, country..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 bg-white"
            data-testid="admin-search-input"
          />
        </div>

        <Tabs defaultValue="quotes">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-4">
            <TabsList data-testid="admin-tabs">
              <TabsTrigger value="quotes" data-testid="tab-quotes">Quotes ({filteredQuotes.length})</TabsTrigger>
              <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts ({filteredContacts.length})</TabsTrigger>
            </TabsList>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={exportQuotes} data-testid="export-quotes-btn">
                <Download className="w-4 h-4 mr-2" /> Export Quotes
              </Button>
              <Button variant="outline" size="sm" onClick={exportContacts} data-testid="export-contacts-btn">
                <Download className="w-4 h-4 mr-2" /> Export Contacts
              </Button>
            </div>
          </div>

          {/* Quotes tab */}
          <TabsContent value="quotes">
            {loading ? (
              <LoadingBlock />
            ) : filteredQuotes.length === 0 ? (
              <EmptyBlock message="No quote requests yet." />
            ) : (
              <div className="space-y-3">
                {filteredQuotes.map((q) => (
                  <div
                    key={q.id}
                    className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md transition-shadow duration-200"
                    data-testid={`quote-row-${q.id}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="font-display font-bold text-lg text-[#0f172a]">{q.name}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{fmtDate(q.created_at)}</div>
                      </div>
                      <div className="inline-flex items-center gap-1.5 bg-sky-50 text-[#1e5ba8] text-xs font-semibold px-2.5 py-1 rounded-full">
                        <Package className="w-3.5 h-3.5" /> {q.product} · {q.quantity}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3 text-sm text-slate-700">
                      <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-[#1e5ba8]" /> <a href={`mailto:${q.email}`} className="hover:underline">{q.email}</a></div>
                      <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-[#1e5ba8]" /> {q.phone}</div>
                      <div className="flex items-center gap-2"><Globe2 className="w-4 h-4 text-[#1e5ba8]" /> {q.country}</div>
                    </div>
                    {q.message && (
                      <div className="mt-3 bg-slate-50 rounded-md p-3 text-sm text-slate-600 whitespace-pre-wrap">{q.message}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Contacts tab */}
          <TabsContent value="contacts">
            {loading ? (
              <LoadingBlock />
            ) : filteredContacts.length === 0 ? (
              <EmptyBlock message="No contact messages yet." />
            ) : (
              <div className="space-y-3">
                {filteredContacts.map((c) => (
                  <div
                    key={c.id}
                    className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md transition-shadow duration-200"
                    data-testid={`contact-row-${c.id}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="font-display font-bold text-lg text-[#0f172a]">{c.name}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{fmtDate(c.created_at)}</div>
                      </div>
                      <div className="inline-flex items-center gap-1.5 bg-sky-50 text-[#1e5ba8] text-xs font-semibold px-2.5 py-1 rounded-full">
                        {c.subject}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-3 text-sm">
                      <Mail className="w-4 h-4 text-[#1e5ba8]" />
                      <a href={`mailto:${c.email}`} className="hover:underline text-slate-700">{c.email}</a>
                    </div>
                    <div className="mt-3 bg-slate-50 rounded-md p-3 text-sm text-slate-600 whitespace-pre-wrap">{c.message}</div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function LoadingBlock() {
  return (
    <div className="flex items-center justify-center py-16 text-slate-500">
      <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading...
    </div>
  );
}

function EmptyBlock({ message }) {
  return (
    <div className="bg-white border border-dashed border-slate-300 rounded-xl py-14 text-center text-slate-500 text-sm">
      {message}
    </div>
  );
}
